import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Tiny wrapper around react-router's useSearchParams that gives a single
 * string query-param a useState-like API. Surviving a page refresh is the
 * default goal; if you want the value to NOT be in the URL, use useState.
 *
 * Setting the value to `defaultValue` removes the param from the URL so
 * shared links stay clean.
 *
 * Usage:
 *   const [year, setYear] = useUrlState('year', String(currentYear()));
 *   const yearNum = parseInt(year);
 */
export function useUrlState(
  key: string,
  defaultValue: string,
): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(key) ?? defaultValue;

  const setValue = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (next === defaultValue || next === '') out.delete(key);
          else out.set(key, next);
          return out;
        },
        { replace: true },
      );
    },
    [key, defaultValue, setSearchParams],
  );

  return [value, setValue];
}

/**
 * Multi-key variant: returns the current snapshot of all params, plus a
 * setter that patches one or more keys at once. Empty string or matching
 * defaults remove the key from the URL.
 */
export function useUrlParams<T extends Record<string, string>>(
  defaults: T,
): [T, (patch: Partial<T>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo(() => {
    const out = { ...defaults };
    for (const k of Object.keys(defaults) as Array<keyof T>) {
      const v = searchParams.get(String(k));
      if (v !== null) (out as Record<string, string>)[String(k)] = v;
    }
    return out;
  }, [searchParams, defaults]);

  const patch = useCallback(
    (p: Partial<T>) => {
      setSearchParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(p)) {
            if (v === undefined) continue;
            if (v === '' || v === defaults[k as keyof T]) out.delete(k);
            else out.set(k, String(v));
          }
          return out;
        },
        { replace: true },
      );
    },
    [defaults, setSearchParams],
  );

  return [values, patch];
}
